import React from 'react';
import { sounds } from '../utils/audio';

export default function YouTubeSection() {
  const vocabulary = [
    { en: 'Rhymes', te: 'పాటలు', ta: 'பாடல்கள்' },
    { en: 'Numbers', te: 'అంకెలు', ta: 'எண்கள்' },
    { en: 'Colors', te: 'రంగులు', ta: 'நிறங்கள்' },
    { en: 'Animals', te: 'జంతువులు', ta: 'விலங்குகள்' },
    { en: 'Fruits', te: 'పండ్లు', ta: 'பழங்கள்' },
    { en: 'Body parts', te: 'శరీర భాగాలు', ta: 'உடல் பாகங்கள்' },
    { en: 'Stories', te: 'కథలు', ta: 'கதைகள்' },
  ];

  return (
    <section id="youtube" className="py-16 lg:py-24 bg-[#F1F7F5] text-[#17323B] border-t-2 border-[#D0E1DC] relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Pill Badge */}
        <div className="flex items-center justify-between mb-8">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#D0E1DC] shadow-xs text-xs font-bold text-[#4A666E]">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span>Kiki and Kids on YouTube</span>
          </div>
          <span className="text-xs font-bold text-[#4A666E]">
            Ages 1 to 5 • Made for Kids
          </span>
        </div>

        {/* Hero Row: Heading + Glyph Tiles */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center pb-16">
          
          {/* Left Text */}
          <div className="lg:col-span-7 space-y-5">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black text-[#17323B] leading-tight tracking-tight">
              Telugu and Tamil, <br className="hidden sm:block" />
              taught slowly.
            </h2>
            <p className="text-base sm:text-lg text-[#4A666E] font-medium leading-relaxed max-w-xl">
              Rhymes, letters, colors and stories with Miss Kiki, made for children aged 1 to 5. 
              Calm enough for small attention spans, familiar enough to feel like home.
            </p>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3.5 pt-2">
              <a
                href="https://www.youtube.com/results?search_query=Kiki+and+Kids+Telugu"
                target="_blank"
                rel="noreferrer"
                onClick={() => sounds.playSound('tap')}
                className="btn-bouncy px-6 py-3.5 rounded-full bg-[#E9A526] hover:bg-amber-500 text-[#2A1B00] font-heading font-bold text-base shadow-sm flex items-center gap-2"
              >
                <span>Watch in</span>
                <span className="font-te text-lg">తెలుగు</span>
                <span>→</span>
              </a>
              <a
                href="https://www.youtube.com/results?search_query=Kiki+and+Kids+Tamil"
                target="_blank"
                rel="noreferrer"
                onClick={() => sounds.playSound('tap')}
                className="btn-bouncy px-6 py-3.5 rounded-full bg-[#1F7F92] hover:bg-teal-700 text-white font-heading font-bold text-base shadow-sm flex items-center gap-2"
              >
                <span>Watch in</span>
                <span className="font-ta text-lg">தமிழ்</span>
                <span>→</span>
              </a>
            </div>
          </div>

          {/* Right Alphabet Tiles */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            <div className="rounded-3xl p-6 bg-[#E9A526] text-[#2A1B00] shadow-md flex flex-col justify-between aspect-square animate-float">
              <span className="font-te text-6xl sm:text-7xl font-black">అ</span>
              <p className="text-xs font-semibold leading-tight opacity-90">
                The Telugu alphabet begins here
              </p>
            </div>
            <div className="rounded-3xl p-6 bg-[#1F7F92] text-white shadow-md flex flex-col justify-between aspect-square mt-6 animate-float" style={{ animationDelay: '1.5s' }}>
              <span className="font-ta text-6xl sm:text-7xl font-black">அ</span>
              <p className="text-xs font-semibold leading-tight opacity-90">
                The Tamil alphabet begins here
              </p>
            </div>
          </div>

        </div>

        {/* Meet Miss Kiki subsection */}
        <div className="pt-12 border-t border-[#D0E1DC] grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-4">
            <h3 className="text-2xl sm:text-3xl font-heading font-bold text-[#17323B]">
              Meet Miss Kiki
            </h3>
            <span className="text-xs font-bold text-[#4A666E] uppercase tracking-wider block mt-1">
              Host &amp; Gentle Storyteller
            </span>
          </div>
          <div className="md:col-span-8 space-y-4 text-base text-[#4A666E] font-medium leading-relaxed">
            <p>
              Miss Kiki hosts every video on both channels. She sings the rhymes, shows each picture, and says each word slowly enough for a toddler to try it back.
            </p>
            <p>
              In the <strong className="text-[#17323B]">Magic Bag</strong> videos, she pulls something out of the bag, names it, and lets the child say it with her before the next surprise.
            </p>
          </div>
        </div>

        {/* Words Children Learn First table */}
        <div className="mt-14 pt-12 border-t border-[#D0E1DC]">
          <div className="mb-6">
            <h3 className="text-2xl sm:text-3xl font-heading font-bold text-[#17323B]">
              Words children learn first
            </h3>
            <p className="text-sm text-[#4A666E] font-medium mt-1">
              Every video teaches a small set of words. These are some of the topics, in both languages.
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl bg-white border border-[#D0E1DC] shadow-xs">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#D0E1DC] bg-[#FAFDFB]">
                  <th className="py-3.5 px-5 text-sm font-bold text-[#4A666E]">English</th>
                  <th className="py-3.5 px-5 text-sm font-bold text-[#8A5A00]">తెలుగు (Telugu)</th>
                  <th className="py-3.5 px-5 text-sm font-bold text-[#176E80]">தமிழ் (Tamil)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D0E1DC] text-base">
                {vocabulary.map((row, idx) => (
                  <tr key={idx} className="hover:bg-[#F1F7F5]/50 transition-colors">
                    <td className="py-3 px-5 font-bold text-[#17323B]">{row.en}</td>
                    <td className="py-3 px-5 font-te text-lg text-[#8A5A00] font-semibold">{row.te}</td>
                    <td className="py-3 px-5 font-ta text-lg text-[#176E80] font-semibold">{row.ta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Calm By Design */}
        <div className="mt-14 pt-12 border-t border-[#D0E1DC]">
          <div className="mb-6">
            <h3 className="text-2xl sm:text-3xl font-heading font-bold text-[#17323B]">
              Calm by design
            </h3>
            <p className="text-sm text-[#4A666E] font-medium mt-1">
              Most children's videos compete for attention. These ones try not to.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="p-5 rounded-2xl bg-white border border-[#D0E1DC] space-y-2">
              <span className="font-heading font-bold text-lg text-[#17323B] block">Slow pace</span>
              <p className="text-xs sm:text-sm text-[#4A666E] leading-relaxed">
                One idea at a time, repeated with room to breathe. No flashing cuts and no loud effects.
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-white border border-[#D0E1DC] space-y-2">
              <span className="font-heading font-bold text-lg text-[#17323B] block">Made for Kids</span>
              <p className="text-xs sm:text-sm text-[#4A666E] leading-relaxed">
                Both channels are set as Made for Kids on YouTube, turning off comments and personalized ads.
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-white border border-[#D0E1DC] space-y-2">
              <span className="font-heading font-bold text-lg text-[#17323B] block">Mother tongue</span>
              <p className="text-xs sm:text-sm text-[#4A666E] leading-relaxed">
                For families at home and abroad who want their children to hear Telugu and Tamil every day.
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-white border border-[#D0E1DC] space-y-2">
              <span className="font-heading font-bold text-lg text-[#17323B] block">Watch together</span>
              <p className="text-xs sm:text-sm text-[#4A666E] leading-relaxed">
                Made to sit beside. Sing along, point at the picture, and say the word again.
              </p>
            </div>
          </div>
        </div>

        {/* Channel Cards */}
        <div className="mt-14 pt-12 border-t border-[#D0E1DC]">
          <h3 className="text-2xl sm:text-3xl font-heading font-bold text-[#17323B] mb-6">
            Pick a channel to watch
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <a
              href="https://www.youtube.com/results?search_query=Kiki+and+Kids+Telugu"
              target="_blank"
              rel="noreferrer"
              onClick={() => sounds.playSound('pop')}
              className="btn-bouncy p-6 rounded-3xl bg-white border-2 border-[#E9A526] shadow-sm hover:shadow-md flex flex-col justify-between group"
            >
              <div>
                <span className="font-te text-4xl text-[#8A5A00] font-black block mb-2">తెలుగు</span>
                <span className="font-heading font-black text-xl text-[#17323B] block">
                  Kiki and Kids Telugu
                </span>
                <p className="text-xs text-[#4A666E] font-medium mt-1">
                  Nursery rhymes, Telugu varnamala, animal sounds, and playful vocabulary.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between text-xs font-bold text-[#8A5A00]">
                <span>Watch on YouTube</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </a>

            <a
              href="https://www.youtube.com/results?search_query=Kiki+and+Kids+Tamil"
              target="_blank"
              rel="noreferrer"
              onClick={() => sounds.playSound('pop')}
              className="btn-bouncy p-6 rounded-3xl bg-white border-2 border-[#1F7F92] shadow-sm hover:shadow-md flex flex-col justify-between group"
            >
              <div>
                <span className="font-ta text-4xl text-[#176E80] font-black block mb-2">தமிழ்</span>
                <span className="font-heading font-black text-xl text-[#17323B] block">
                  Kiki and Kids Tamil
                </span>
                <p className="text-xs text-[#4A666E] font-medium mt-1">
                  Tamil uyir ezhuthukkal, sweet songs, daily words, and joyful storytelling.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between text-xs font-bold text-[#176E80]">
                <span>Watch on YouTube</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
